import React, { useEffect, useState, useMemo } from 'react';
import { User } from 'types';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { CircularProgress, Box, TextField } from '@mui/material';
import { usePermissionCheck } from '../../../../../core/hooks/usePermissionCheck';
import { Button } from 'components/ui/button';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button as MuiButton,
} from '@mui/material';
import CreateUserSettings from './CreateUserSettings';
import ShowUserSetting from './ShowUserSettings/ShowUserSetting';
import styles from '../GeneralSettings.module.scss';
import EditUserSettings from './EditUserSettings/EditUserSetting';
import { useUserCrud } from 'core/hooks/useUserCrud';
import { getUsersByWorkplace } from 'core/API/checklist';

export interface UserSettingsProps {
	handleFocusChange?: (focus: 'organization' | 'workplace' | 'user' | null) => void;
}

function UserSettings(props: UserSettingsProps) {
	const { handleFocusChange } = props;
	const { hasEditPermission, hasViewPermission, hasDeletePermission } = usePermissionCheck();
	const canViewUser = hasViewPermission('User');
	const canEditUser = hasEditPermission('User');
	const canDeleteUser = hasDeletePermission('User');

	// Clean hierarchy name: remove parenthetical content and standalone 'node'
	const cleanHierarchyName = (raw?: string) => {
		if (!raw) return '';
		let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ');
		s = s.replace(/\bnode\b/gi, ' ');
		return s.replace(/\s+/g, ' ').trim();
	};

	// Use the custom hook for user CRUD operations
	const {
		users,
		loading: usersLoading,
		error: usersError,
		fetchUsers,
		deleteUser: apiDeleteUser,
		clearError,
	} = useUserCrud();

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [userToDelete, setUserToDelete] = useState<User | null>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');

	// Set of user IDs that belong to the current workplace. If null, UI will not filter by workplace.
	const [workplaceUserIdSet, setWorkplaceUserIdSet] = useState<Set<number> | null>(null);

	// Build filtered + sorted users list (un-sliced) and derive pagination from it
	const filteredSortedUsers = useMemo(() => {
		if (!users) return [];

		// First filter out users with restricted security roles
		let filteredUsers = users.filter((user) => {
			const roleName = user?.securityRole?.name;
			return (
				!roleName ||
				!['Mabel Support', 'Mabel Admin', 'BYOD Support', 'BYOD Admin'].includes(roleName)
			);
		});

		// Apply search filter
		if (searchTerm.trim()) {
			const lowerSearch = searchTerm.toLowerCase();
			filteredUsers = filteredUsers.filter((user) => {
				const firstName = (user?.firstName || '').toLowerCase();
				const lastName = (user?.lastName || '').toLowerCase();
				const hierarchyName = cleanHierarchyName(user?.hierarchy?.name).toLowerCase();
				const roleName = (user?.securityRole?.name || '').toLowerCase();
				const email = (user?.email || '').toLowerCase();
				return (
					firstName.includes(lowerSearch) ||
					lastName.includes(lowerSearch) ||
					hierarchyName.includes(lowerSearch) ||
					roleName.includes(lowerSearch) ||
					email.includes(lowerSearch)
				);
			});
		}

		// Sort users so that:
		// 1) non-separated users come first
		// 2) within that, active users come first
		// 3) then alphabetically by lastName, then firstName
		const sortedUsers = [...filteredUsers].sort((a, b) => {
			// separation: false (0) -> should come before true (1)
			const sepA = a.separation ? 1 : 0;
			const sepB = b.separation ? 1 : 0;
			if (sepA !== sepB) return sepA - sepB;

			// active: true -> should come before false
			const aActive = a.userType?.active ? 1 : 0;
			const bActive = b.userType?.active ? 1 : 0;
			if (aActive !== bActive) return bActive - aActive;

			// finally sort by name
			const lastNameComparison = (a.lastName || '').localeCompare(b.lastName || '', undefined, {
				sensitivity: 'base',
			});
			if (lastNameComparison !== 0) return lastNameComparison;
			return (a.firstName || '').localeCompare(b.firstName || '', undefined, {
				sensitivity: 'base',
			});
		});

		// If we have a workplace id set, filter to only those users
		let finalUsers = sortedUsers;
		if (workplaceUserIdSet instanceof Set) {
			finalUsers = finalUsers.filter((u) => workplaceUserIdSet.has(Number(u?.id)));
		}

		return finalUsers;
	}, [users, workplaceUserIdSet, searchTerm]);

	// Fetch users and workplace-user mapping on component mount
	useEffect(() => {
		const loadUsers = async () => {
			try {
				// Load the main users list via the hook first to set loading state
				await fetchUsers({
					orderBy: 'id',
					direction: 'ASC',
					limit: 50,
					page: 1,
					active: true,
				});

				// Then attempt to fetch workplace-specific users to filter the main list
				try {
					const wpUsers = await getUsersByWorkplace();
					const idSet = new Set<number>(
						wpUsers.map((u: any) => Number(u?.id)).filter((n: number) => !Number.isNaN(n))
					);
					setWorkplaceUserIdSet(idSet);
				} catch (wpErr) {
					console.error('Failed to load workplace users:', wpErr);
					// If workplace users fail, clear the set so UI won't filter
					setWorkplaceUserIdSet(null);
				}
			} catch (err: any) {
				console.error('Failed to fetch users or workplace users:', err);
			}
		};

		if (viewMode === 'list') {
			loadUsers();
		}
	}, [viewMode, fetchUsers]);

	const handleDeleteClick = (user: User) => {
		if (!canDeleteUser) return;
		console.log(`Delete User ${user.id}`);
		setUserToDelete(user);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (userToDelete) {
			try {
				await apiDeleteUser(userToDelete.id);

				setDeleteDialogOpen(false);
				setUserToDelete(null);
			} catch (error) {
				// Error is already handled by the hook
				console.error('Failed to delete user:', error);
			}
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setUserToDelete(null);
		clearError();
	};

	const handleCreateClick = () => {
		if (!canEditUser) return;
		handleFocusChange?.('user');
		setViewMode('create');
	};

	const handleBackClick = () => {
		handleFocusChange?.(null);
		setViewMode('list');
		setSelectedUser(null);
	};

	const handleShowClick = (user: User) => {
		if (!canViewUser) return;
		console.log(`Show User ${user.id}`);
		handleFocusChange?.('user');
		setSelectedUser(user);
		setViewMode('show');
	};

	const handleEditClick = (user: User) => {
		if (!canEditUser) return;
		console.log(`Edit User ${user.id}`);
		handleFocusChange?.('user');
		setSelectedUser(user);
		setViewMode('edit');
	};

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* Users Card */}
					<div className={styles.workplaceCard}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Users</h2>
						<div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
						<TextField
							label="Search"
							placeholder="Search users..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							variant="outlined"
									// className={styles.input}
							size="small"
							style={{
								minWidth: '250px',
								padding: 'none',
								}}
							/>
							<Button
								className={styles.addButton}
								onClick={handleCreateClick}
								disabled={!canEditUser}
								title={canEditUser ? 'Add User' : 'You do not have permission for this action'}
								style={{
									opacity: canEditUser ? 1 : 0.5,
									cursor: canEditUser ? 'pointer' : 'not-allowed',
								}}
							>
								Add
							</Button>
						</div>
					</div>

					{usersError && <div className={styles.errorMessage}>{usersError}</div>}

					{usersLoading ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									minHeight: '300px',
								}}
							>
								<CircularProgress color="inherit" size={20} />
							</Box>
						) : users && users.length > 0 ? (
							<div
								className={styles.tableContainer}
								style={{
									maxHeight: '500px',
									overflow: 'auto', // Enable both vertical and horizontal scrolling
								}}
							>
								<table
									className={`${styles.workplaceTable} ${styles.userTable}`}
									style={{ tableLayout: 'auto' }}
								>
									<thead>
										<tr>
											<th>First Name</th>
											<th>Last Name</th>
											<th>Hierarchy</th>
											<th style={{ width: '80px' }}>Active</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>View</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
										</tr>
									</thead>
									<tbody>
										{filteredSortedUsers.map((user: User) => (
											<tr key={user?.id}>
												<td style={{ whiteSpace: 'nowrap' }}>{user?.firstName}</td>
												<td style={{ whiteSpace: 'nowrap' }}>{user?.lastName}</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													{cleanHierarchyName(user?.hierarchy?.name)}
												</td>
												<td style={{ textAlign: 'center' }}>
													{user?.separation ? 'Separated' : user?.userType?.active ? 'Yes' : 'No'}
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleShowClick(user)}
															title={
																canViewUser
																	? 'View User'
																	: 'You do not have permission for this action'
															}
															disabled={!canViewUser}
															style={{
																opacity: canViewUser ? 1 : 0.5,
																cursor: canViewUser ? 'pointer' : 'not-allowed',
															}}
														>
															<Visibility className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleEditClick(user)}
															title={
																canEditUser
																	? 'Edit User'
																	: 'You do not have permission for this action'
															}
															disabled={!canEditUser}
															style={{
																opacity: !canEditUser ? 0.5 : 1,
																cursor: !canEditUser ? 'not-allowed' : 'pointer',
															}}
														>
															<Edit className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ padding: '8px', width: '48px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleDeleteClick(user)}
															title={
																canDeleteUser
																	? 'Delete User'
																	: 'You do not have permission for this action'
															}
															disabled={!canDeleteUser}
															style={{
																opacity: !canDeleteUser ? 0.5 : 1,
																cursor: !canDeleteUser ? 'not-allowed' : 'pointer',
															}}
														>
															<Delete className={styles.deleteIcon} />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : !usersError ? (
							<div className={styles.noDataMessage}>No users found.</div>
						) : null}

						{/* Delete Confirmation Dialog */}
						<Dialog
							open={deleteDialogOpen}
							onClose={handleDeleteCancel}
							aria-labelledby="delete-dialog-title"
							aria-describedby="delete-dialog-description"
						>
							<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
							<DialogContent>
								<DialogContentText id="delete-dialog-description">
									Are you sure you want to delete this user?
									{userToDelete &&
										` ${userToDelete.firstName} ${userToDelete.lastName} will be permanently removed.`}
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								<MuiButton onClick={handleDeleteCancel} color="primary">
									Cancel
								</MuiButton>
								<MuiButton onClick={handleDeleteConfirm} color="error" autoFocus>
									Delete
								</MuiButton>
							</DialogActions>
						</Dialog>
					</div>
				</>
			);
		case 'create':
			return (
				<div className={styles.createWorkplaceContainer}>
					<CreateUserSettings
						backButtonClick={handleBackClick}
						onUserCreated={() => {

							// Refetch users after creation
							fetchUsers({
								orderBy: 'id',
								direction: 'ASC',
								limit: 50,
								page: 1,
								active: true,
							});
						}}
					/>
				</div>
			);
		case 'edit':
			return (
				<div className={styles.showWorkplaceContainer}>
					{selectedUser && (
						<EditUserSettings user={selectedUser} handleBackClick={handleBackClick} />
					)}
				</div>
			);
		default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<h2 className={styles.formTitle}>User Details</h2>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedUser && <ShowUserSetting user={selectedUser} />}
				</div>
			);
	}
}

export default UserSettings;
